import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Input from '../../../components/Input/Input';
import { Button } from 'src/components/ui/button';
import { useAuth } from 'src/provider/AuthProvider';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useLoader } from 'src/provider/LoaderProvider';
import { PATH_HOME, PATH_ONBOARDING, PATH_REGISTER } from 'src/constants/spa-routes';
import { getLeadSourceCampaign, getLeadSourceName, getLeadSourceType } from 'src/utils/utm.utils';
import { useDispatch } from 'react-redux';
import { setBigin } from 'src/store/slices/onboardingSlice';
import { useUpdateAccountsMutation } from 'src/services/accountsService';
import { useGetUsersMeQuery } from 'src/services/usersService';

export const RegisterGoogleForm = () => {
  const [biginRequest, setBiginRequest] = React.useState(null);
  const navigate = useNavigate();
  const loader = useLoader();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { data: userInfo, isLoading: isLoadingUsersMe } = useGetUsersMeQuery(undefined, {
    skip: !user,
  });
  const [
    updateAccountInfo,
    { isLoading: isLoadingUpdateAccountInfo, isSuccess: isSuccessUpdateAccount },
  ] = useUpdateAccountsMutation();

  useEffect(() => {
    if (isLoadingUsersMe || isLoadingUpdateAccountInfo) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUsersMe, isLoadingUpdateAccountInfo]);

  useEffect(() => {
    if (isSuccessUpdateAccount && biginRequest) {
      const skipOnboarding = sessionStorage.getItem('skip_onboarding');

      if (window.gtag) {
        window.gtag('event', 'signup_success');
        window.gtag('event', 'conversion', { send_to: 'AW-11237889467/1lznCL2ozOgbELur0u4p' });
      }

      if (skipOnboarding) {
        navigate(PATH_HOME.ROOT, { replace: true });
      } else {
        (async () => {
          const response = await fetch('/api/bigin', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(biginRequest),
          }).then((res) => res.json());

          if (response.success) {
            dispatch(setBigin({ pipelineId: response.pipelineId, data: biginRequest }));
          }

          navigate(PATH_ONBOARDING.ROOT, { replace: true });
        })();
      }
    }
  }, [isSuccessUpdateAccount, biginRequest]);

  const {
    handleSubmit,
    trigger,
    control,
    clearErrors,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      companyName: '',
    },
    resolver: yupResolver(
      object({
        companyName: stringScheme({
          required: true,
          min: 3,
          minMessage: 'Min. 3 characters',
        }).label('Company name'),
      }),
    ),
  });

  const submit = async (data) => {
    if (isValid) {
      loader.show();

      const newBiginRequest = {
        email: userInfo.email,
        firstName: userInfo.first_name || '',
        lastName: userInfo.last_name || '',
        companyName: data.companyName,
        leadSourceName: getLeadSourceName(),
        leadSourceType: getLeadSourceType(),
        leadSourceCampaign: getLeadSourceCampaign(),
        sourceUrl: window.location.href,
        tags: ['Cabinet'],
      };

      setBiginRequest(newBiginRequest);

      await updateAccountInfo({
        company_name: data.companyName,
      });
    }
  };

  return (
    <React.Fragment>
      <form
        id="registration-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(submit)(e);
        }}
      >
        <div className={'input-container flex flex-col gap-[3px] mt-[3px]'}>
          <Controller
            control={control}
            name="companyName"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Company Name'}
                placeholder={'Company'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('companyName')}
                onFocus={() => clearErrors('companyName')}
              />
            )}
          />
        </div>
      </form>
      <div className="flex flex-col">
        <Button
          form="registration-form"
          type="submit"
          className="mt-[25px] w-full min-h-[50px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]"
        >
          <div>Continue</div>
        </Button>
        <div>
          <p className={'text-center pt-5'}>
            <Link className={'text-xs text-[#3F60B3] hover:underline'} to={PATH_REGISTER.SIGN_UP}>
              ← Back
            </Link>
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};
